'use client';

import { useState, useMemo } from 'react';
import { blogPosts } from '../../../_data/posts';
import { isPublished, BlogPost, PostMeta } from '../../../_data/posts.types';
import BlogCard from './BlogCard';
import CategoryFilter from './CategoryFilter';

function formatDate(publishedDate?: string, year?: string): string {
  if (publishedDate) {
    return new Date(publishedDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  return year || '';
}

const BlogList = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  // Filter only published posts, then sort by date (newest first)
  const sortedBlogPosts = useMemo(
    () =>
      Object.entries(blogPosts)
        .filter(([, post]) => isPublished(post.meta))
        .sort((a, b) => {
          const dateA = a[1].meta.publishedDate || '';
          const dateB = b[1].meta.publishedDate || '';
          return dateB.localeCompare(dateA);
        }) as Array<[string, BlogPost]>,
    [],
  );

  // Extract unique categories from published posts
  const categories = useMemo(() => {
    const seen = new Map<string, string>();
    for (const [, post] of sortedBlogPosts) {
      if (!seen.has(post.categorySlug)) {
        seen.set(post.categorySlug, post.category);
      }
    }
    return Array.from(seen, ([slug, label]) => ({ slug, label }));
  }, [sortedBlogPosts]);

  // Filter posts by active category
  const filteredPosts = useMemo(
    () =>
      activeCategory === 'all'
        ? sortedBlogPosts
        : sortedBlogPosts.filter(([, post]) => post.categorySlug === activeCategory),
    [activeCategory, sortedBlogPosts],
  );

  return (
    <>
      <CategoryFilter categories={categories} activeCategory={activeCategory} onCategoryChange={setActiveCategory} />
      <ul className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto'>
        {filteredPosts.map(([key, post]) => (
          <li key={key} className='list-none flex'>
            <BlogCard
              key={key}
              keyValue={key}
              title={post.title}
              description={post.meta?.description || ''}
              category={post.category}
              categorySlug={post.categorySlug}
              date={formatDate(post.meta?.publishedDate, (post.meta as PostMeta)?.year)}
              author={post.meta?.author || ''}
            />
          </li>
        ))}
      </ul>
    </>
  );
};

export default BlogList;
